import { Box } from 'grommet';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Field from '../../types/Field';
import { navigateBackToProfile } from '../../service/common/utils';
import { FieldStep } from '../Field/FieldStep';
import { HerdStep } from '../Herd/HerdStep';
import DateRangeModal from '../Modals/DateRangeModal/DateRangeModal';
import ShowMeHowModal from '../Modals/ShowMeHowModal';
import { PaddockStep } from '../Paddock/PaddockStep';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch } from 'react-redux';
import WizardHeader from './components/WizardHeader';
import SaveAndLeaveModal from '../Modals/SaveAndLeaveModal';
import { Step } from '../../types/Step';
import NextStepButton from './components/NextStepButton';
import FieldStepButtons from './components/FieldStepButtons';
import { EnumProcessingStatus } from '../../service/common/types';
import { putTrainingRegions, saveFields } from '../../../../redux/actions/map-actions/field/field-action';
import { notifyUpdate, upsertShapefile } from '../../../../service/map/map.service';
import { useAppSelector } from '../../../../redux';

export const Wizard = ({ setFieldToDelete }: any): JSX.Element => {
  const { t } = useTranslation();
  const { journeyId } = useParams();
  const { brFinalizeBoundaries, brBoundaryApproval, enableAsyncFieldProcessing } = useFlags();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const journey = useAppSelector((state) => state.producer.journeyInformation);
  const state = useAppSelector((state) => state.map.state);
  const trainingRegionsTouched = useAppSelector((state) => state.map.trainingRegionsTouched);
  const trainingRegions = useAppSelector((state) => state.map.trainingRegions);
  const ineligibleAreasFinalized = useAppSelector((state) => state.map.ineligibleAreasFinalized);
  const processingStatus = useAppSelector((state) => state.map.processingStatus);
  const fields = useAppSelector((state) => state.map.fields);
  const deletedFields = useAppSelector((state) => state.map.deletedFields);
  const updatedFieldBoundaries = useAppSelector((state) => state.map.updatedFieldBoundaries);
  const contractPaymentOption = useAppSelector((state) => state.map.contractPaymentOption);
  const [stepIndex, setStepIndex] = useState<number>(state === 'SECOND_SETUP' ? 1 : 0);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [saveLeaveLoading, setSaveLeaveLoading] = useState<boolean>(false);
  const [showDateRangeModal, setShowDateRangeModal] = useState<boolean>(false);
  const [showMeHowModal, setShowMeHowModal] = useState<boolean>(false);
  const [showSaveAndLeaveModal, setShowSaveAndLeaveModal] = useState<boolean>(false);
  const [field, setField] = useState<Field | undefined>(
    fields.find((field) => field.properties && (field.properties['editing'] || field.properties['selected'])),
  );
  const country = useAppSelector((state) => state.producer.producerInformation?.country ?? 'us');
  const producerData = useAppSelector((state) => state.producer.producerInformation);
  const shapefileStatus = useAppSelector((state) => state.map.shapefileStatus);

  const isChannelPartner = useAppSelector((state) => !!state.user.userData.channelPartnerId);
  const user = useAppSelector((state) => state.user.userData);

  const steps: Step[] = [
    {
      name:
        country === 'us' && enableAsyncFieldProcessing && !ineligibleAreasFinalized
          ? processingStatus !== EnumProcessingStatus.PROCESSED
            ? 'FieldsProcess'
            : processingStatus === EnumProcessingStatus.PROCESSED
            ? 'FinalizeFieldsProcess'
            : 'Fields'
          : 'Fields',
      title: t('fieldFlow.mainWizard.Fields.title'),
      levels: ['Farm'],
      component: <FieldStep t={t} setFieldToDelete={setFieldToDelete} />,
      ignoreStep: state === 'SECOND_SETUP',
      hideButton:
        isChannelPartner ||
        (country === 'br' && (!brFinalizeBoundaries || 'Data Entry' !== producerData?.caseStatus)) ||
        (country !== 'br' && !['Stratification & Data Collection', 'Document & Data Collection'].includes(producerData?.caseStatus)),
      hideBorder: true,
    },
    {
      name: 'Paddocks',
      title: t('fieldFlow.mainWizard.Paddocks.title'),
      levels: ['Field'],
      component: <PaddockStep setShowMeHow={setShowMeHowModal} selectedField={field} />,
      hideBorder: true,
    },
    {
      name: 'Herds',
      title: t('fieldFlow.mainWizard.Herds.title'),
      levels: ['Paddock'],
      component: <HerdStep />,
    },
  ];
  const currentStep = steps[stepIndex];
  const win: any = window;
  win.steps = steps;

  // TODO state never goes to second setup since grazing is disabled
  // useEffect(() => {
  //   if (state === 'SECOND_SETUP') {
  //     setStepIndex(1);
  //     setWizardStep('PADDOCK');
  //     setShowDateRangeModal(true);
  //   }
  // }, [state]);

  useEffect(() => {
    let field = fields.find((field) => field.properties?.['editing']);

    if (!field) {
      field = fields.find((field) => field.properties?.['selected']);
    }

    setField(field);
  }, [fields, setField]);

  function shouldNotifyUpdate() {
    if (brBoundaryApproval) {
      return updatedFieldBoundaries && country === 'br' && (shapefileStatus === 'ANALYSIS_DONE' || shapefileStatus === 'APPROVED');
    }
    return updatedFieldBoundaries && country === 'br' && producerData.attributes.type === 'Opportunity' && producerData['StageName'] !== 'Closed Won';
  }

  function saveAndLeave() {
    if (!journeyId || !journey) return;

    setSaveLeaveLoading(true);
    //Store notify status before save
    const shouldNotify = shouldNotifyUpdate();
    let saveShapefile;
    if (shapefileStatus !== 'GATHERING' && updatedFieldBoundaries) {
      saveShapefile = () => upsertShapefile(journey, 'GATHERING', country, contractPaymentOption);
    }

    const navigateBack = () => {
      if (shouldNotify) {
        return notifyUpdate(
          t,
          producerData['Name'],
          `${process.env['REACT_APP_PLATFORM_BASE_URL']}/leads/${journeyId}`,
          user,
          brBoundaryApproval,
        ).then(() => {
          navigateBackToProfile(journeyId, navigate, dispatch);
        });
      }
      return navigateBackToProfile(journeyId, navigate, dispatch);
    };
    dispatch(saveFields(journey, fields, deletedFields, updatedFieldBoundaries, navigateBack, saveShapefile));
    if (trainingRegionsTouched) {
      dispatch(putTrainingRegions(journey, trainingRegions));
    }
  }

  return (
    <>
      {showMeHowModal && <ShowMeHowModal setShowMeHow={setShowMeHowModal} />}
      {showDateRangeModal && (
        <DateRangeModal
          steps={steps}
          stepIndex={stepIndex}
          setStepIndex={setStepIndex}
          setDateRangeModal={setShowDateRangeModal}
          setShowMeHowModal={setShowMeHowModal}
        />
      )}
      {showSaveAndLeaveModal && <SaveAndLeaveModal setShowSaveAndLeaveModal={setShowSaveAndLeaveModal} saveAndLeave={saveAndLeave} />}
      <Box
        width={'40vw'}
        background={'white'}
        justify={'between'}
        style={{
          position: 'absolute',
          right: '24px',
          top: '24px',
          bottom: '24px',
          zIndex: 1,
          padding: '24px',
          borderRadius: '5px',
          minWidth: '560px',
          maxWidth: '800px',
        }}>
        {/*Deactivating breadcrumb, flow currently not working */}
        {/*<BreadCrumb steps={steps} stepIndex={stepIndex} />*/}
        <Box height={'100%'}>
          <WizardHeader currentStep={currentStep} />
          <Box
            margin={currentStep.hideBorder ? '0' : '0 0 0.5rem 0'}
            height={'100%'}
            style={currentStep.hideBorder ? undefined : { borderRadius: '10px' }}
            border={currentStep.hideBorder ? undefined : { color: '#808080', style: 'solid', size: '1px' }}>
            {currentStep.component}
          </Box>
          <Box direction={'row'} justify={'between'} width={'100%'}>
            <Box direction='row' justify={'between'} gap={'8px'} width={currentStep.hideButton ? '100%' : undefined}>
              {stepIndex === 0 ? (
                <FieldStepButtons
                  setShowSaveAndLeaveModal={setShowSaveAndLeaveModal}
                  saveAndLeave={saveAndLeave}
                  saveLeaveLoading={saveLeaveLoading}
                  showLoading={showLoading}
                  shouldNotifyUpdate={shouldNotifyUpdate}
                  setFieldToDelete={setFieldToDelete}
                />
              ) : (
                <Box />
              )}
            </Box>
            <NextStepButton
              currentStep={currentStep}
              stepIndex={stepIndex}
              showLoading={showLoading}
              setShowDateRangeModal={setShowDateRangeModal}
              setStepIndex={setStepIndex}
              steps={steps}
              setShowLoading={setShowLoading}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Wizard;
